const sanitizePercentage = (percentage: number) => {
  const isInvalidOrNegative = !Number.isFinite(+percentage) || percentage < 0;
  const isAboveMaximum = percentage > 100;
  return isInvalidOrNegative ? 0 : isAboveMaximum ? 100 : +percentage;
};

const ProgressCircle = ({
  strokeColor,
  progressPercentage,
  size,
}: {
  strokeColor: string;
  progressPercentage: number;
  size: number;
}) => {
  // Scale all dimensions based on size
  const baseSize = 150; // Original size
  const scale = size / baseSize;

  const circleRadius = 55 * scale;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset =
    ((100 - progressPercentage) * circleCircumference) / 100;

  // Scale center position proportionally
  const centerX = 125 * scale;
  const centerY = 75 * scale;
  const strokeWidth = 25 * scale; // Convert from rem to pixels for scaling

  return (
    <circle
      r={circleRadius}
      cx={centerX}
      cy={centerY}
      fill="transparent"
      stroke={strokeDashoffset !== circleCircumference ? strokeColor : ""}
      strokeWidth={strokeWidth}
      strokeDasharray={circleCircumference}
      strokeDashoffset={progressPercentage ? strokeDashoffset : 0}
      strokeLinecap="round"
    ></circle>
  );
};

const PercentageText = ({
  displayPercentage,
  size,
}: {
  displayPercentage: number;
  size: number;
}) => {
  // Scale font size proportionally
  const baseSize = 150;
  const scale = size / baseSize;
  const fontSize = 1.5 * scale * 16; // Convert rem to pixels and scale

  return (
    <text
      x="50%"
      y="50%"
      dominantBaseline="central"
      textAnchor="middle"
      fontFamily="sans-serif"
      fontSize={fontSize}
      fontWeight={900}
      fill="black"
    >
      {displayPercentage.toFixed(0)}%
    </text>
  );
};

const CircularProgress = ({
  percentage,
  color,
  size = 140, // Default to original size
}: {
  percentage: number;
  color: string;
  size?: number;
}) => {
  const normalizedPercentage = sanitizePercentage(percentage);

  // Scale rotation center proportionally
  const baseSize = 150;
  const scale = size / baseSize;
  const rotationCenter = 100 * scale;

  return (
    <svg width={size} height={size}>
      <g transform={`rotate(-90 ${rotationCenter} ${rotationCenter})`}>
        <ProgressCircle
          strokeColor="#e0e0e0"
          progressPercentage={100}
          size={size}
        />
        <ProgressCircle
          strokeColor={color}
          progressPercentage={normalizedPercentage}
          size={size}
        />
      </g>
      <PercentageText displayPercentage={normalizedPercentage} size={size} />
    </svg>
  );
};

export default CircularProgress;
